import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, Package, Search, CheckCircle2, Clock, Truck, XCircle } from 'lucide-react';
import { SEOHead } from '@/components/seo/SEOHead';
import Page from '@/components/layout/Page';
import { toast } from 'sonner';

interface Order {
  id: string;
  order_number: string;
  customer_email: string;
  customer_name: string;
  delivery_address: any;
  items: any[];
  subtotal: number;
  shipping: number;
  total: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  created_at: string;
}

const statusConfig = {
  pending: { icon: Clock, label: 'Pending', color: 'text-yellow-400' },
  processing: { icon: Package, label: 'Processing', color: 'text-blue-400' },
  shipped: { icon: Truck, label: 'Shipped', color: 'text-purple-400' },
  delivered: { icon: CheckCircle2, label: 'Delivered', color: 'text-emerald-400' },
  cancelled: { icon: XCircle, label: 'Cancelled', color: 'text-red-400' },
};

export default function OrderTracking() {
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const { data, error } = await supabase.functions.invoke('track-order', {
        body: { email: email.toLowerCase().trim() }
      });

      if (error) throw error;

      setOrders((data.orders || []) as Order[]);
      
      if (!data || data.length === 0) {
        toast.info('No orders found for this email address');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatPrice = (price: number) => {
    return `£${price.toFixed(2)}`;
  };

  return (
    <>
      <SEOHead
        title="Track your order | Samboards"
        description="Track your Samboards order status and view order history. Enter your email to see all your orders and their current delivery status."
        canonical="https://samboards.co.uk/order-tracking"
      />
      
      <Page>
        <div className="min-h-screen pt-[96px] px-4 pb-20">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="page-title-gradient text-4xl md:text-5xl font-bold mb-4">
                Track your order
              </h1>
              <p className="text-slate-300 text-lg">
                Enter your email address to view your order history and status
              </p>
            </div>

            {/* Search Form */}
            <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-8 mb-8">
              <form onSubmit={handleSearch} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-slate-200 mb-2 block">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-500"
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#9ff3e3] to-[#8ad0ff] text-black font-semibold hover:shadow-[0_0_40px_rgba(159,243,227,0.6)] transition-all"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Find my orders
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* Orders List */}
            {searched && (
              <div className="space-y-6">
                {orders.length === 0 ? (
                  <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-12 text-center">
                    <Package className="mx-auto h-16 w-16 text-slate-600 mb-4" />
                    <p className="text-slate-300 text-lg">No orders found</p>
                    <p className="text-slate-500 text-sm mt-2">
                      We couldn't find any orders associated with this email address
                    </p>
                  </Card>
                ) : (
                  <>
                    <h2 className="text-2xl font-semibold text-slate-100 mb-4">
                      Your orders ({orders.length})
                    </h2>
                    
                    {orders.map((order) => {
                      const StatusIcon = statusConfig[order.status].icon;
                      
                      return (
                        <Card
                          key={order.id}
                          className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-6 hover:border-emerald-400/40 transition-all"
                        >
                          {/* Order Header */}
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-4 border-b border-slate-700/60">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-100 mb-1">
                                Order {order.order_number}
                              </h3>
                              <p className="text-sm text-slate-400">
                                Placed on {formatDate(order.created_at)}
                              </p>
                            </div>
                            
                            <div className="flex items-center gap-2 mt-4 md:mt-0">
                              <StatusIcon className={`h-5 w-5 ${statusConfig[order.status].color}`} />
                              <span className={`font-semibold ${statusConfig[order.status].color}`}>
                                {statusConfig[order.status].label}
                              </span>
                            </div>
                          </div>

                          {/* Order Items */}
                          <div className="space-y-3 mb-6">
                            {order.items.map((item: any, index: number) => (
                              <div key={index} className="flex justify-between items-center text-sm">
                                <div>
                                  <p className="text-slate-200 font-medium">{item.name}</p>
                                  <p className="text-slate-500">Quantity: {item.quantity}</p>
                                </div>
                                <p className="text-slate-300">{formatPrice(item.price * item.quantity)}</p>
                              </div>
                            ))}
                          </div>

                          {/* Order Total */}
                          <div className="pt-4 border-t border-slate-700/60">
                            <div className="flex justify-between items-center text-lg font-semibold">
                              <span className="text-slate-200">Total</span>
                              <span className="text-emerald-400">{formatPrice(order.total)}</span>
                            </div>
                          </div>

                          {/* Delivery Address */}
                          <div className="mt-4 pt-4 border-t border-slate-700/60">
                            <p className="text-sm text-slate-400 mb-2">Delivery address:</p>
                            <p className="text-sm text-slate-300">
                              {order.delivery_address.address}, {order.delivery_address.city},{' '}
                              {order.delivery_address.postcode}, {order.delivery_address.country}
                            </p>
                          </div>
                        </Card>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Page>
    </>
  );
}
